import { Button, Tooltip } from "@deligoo/ui"
import clsx from "clsx"
import dayjs from "dayjs"
import { CSSProperties, Dispatch, memo, SetStateAction, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { AutoSizer } from "react-virtualized/dist/commonjs/AutoSizer"
import { areEqual, FixedSizeList } from "react-window"

import { useAppDispatch, useAppSelector } from "@/App"
import { Card } from "@/components/Card"
import { Clipboard } from "@/components/Clipboard"
import { ContextMenu, openContextMenu } from "@/components/ContextMenu"
import { Table } from "@/components/Table"
import { usePaymentsContextMenuTrigger } from "@/modules/PaymentContextMenu"
import {
  selectPaymentById,
  selectPaymentsStatus,
  selectSortedSettledPaymentsIds,
  togglePaymentStatus,
} from "@/store/payments"
import { Payment } from "@/types"
import { formatPrice } from "@/utils"

import cls from "../PaymentsView.module.scss"

const TABLE_HEADERS = ["client", "datetime", "order_id", "terminal", "amount", "driver", "status"] as const

const ROW_HEIGHT_PX = 75 as const

function paymentsSorter(a: Payment, b: Payment) {
  const valueA = !a.order_payment_status ? 1 : 0
  const valueB = !b.order_payment_status ? 1 : 0

  return valueB - valueA
}

type PaymentsProps = {
  paymentsDate: string
  setPaymentsDate: Dispatch<SetStateAction<string>>
}

export const Payments = ({ paymentsDate, setPaymentsDate }: PaymentsProps) => {
  const paymentsIds = useAppSelector(selectSortedSettledPaymentsIds(paymentsSorter))
  const paymentsStatus = useAppSelector(selectPaymentsStatus)

  const prevMonth = dayjs(paymentsDate).subtract(1, "month")
  const nextMonth = dayjs(paymentsDate).add(1, "month")

  const tBodyStyle: CSSProperties = useMemo(() => {
    const minHeightValue = paymentsIds.length > 20 ? ROW_HEIGHT_PX * 20 : ROW_HEIGHT_PX * paymentsIds.length

    return {
      minHeight: `${minHeightValue}px`,
    }
  }, [paymentsIds])

  const { t } = useTranslation("payments")

  return (
    <Card box className={clsx(cls.tableCard, "mt-6 flex-grow-1")}>
      <Card.Header className={cls.paymentsHeader}>
        <Button
          onClick={() => setPaymentsDate(prevMonth.toISOString())}
          iconProps={{ name: "chevron-tiny-bold-left" }}
          className={cls.prevMonth}
        >
          {prevMonth.format("MMMM")}
        </Button>

        <Button color="primary" className={cls.currentMonth}>
          {dayjs(paymentsDate).format("MMMM YYYY")}
        </Button>

        <Button
          onClick={() => setPaymentsDate(nextMonth.toISOString())}
          iconProps={{ name: "chevron-tiny-bold-right" }}
          iconSide="right"
          className={cls.nextMonth}
        >
          {nextMonth.format("MMMM")}
        </Button>
      </Card.Header>

      <Table
        minWidth="1400px"
        className={cls.paymentsTable}
        columns="200px minmax(150px, 200px) minmax(300px, 400px) minmax(150px, 200px) minmax(130px, 200px) minmax(250px, 350px) minmax(110px, 300px) minmax(100px, 1fr)"
        loading={paymentsStatus === "loading"}
        error={paymentsStatus === "error"}
        empty={paymentsIds.length === 0}
        virtualized
      >
        <Table.Thead>
          <Table.Tr>
            {TABLE_HEADERS.map((header) => (
              <Table.Th className={clsx(["status"].includes(header) && "text-center")} key={header}>
                {t(`payments_list_headers.${header}`)}
              </Table.Th>
            ))}
            <Table.Th />
          </Table.Tr>
        </Table.Thead>

        <Table.Tbody style={tBodyStyle}>
          <AutoSizer>
            {({ height, width }) => (
              <FixedSizeList
                height={height}
                width={width}
                overscanCount={4}
                itemData={paymentsIds}
                itemCount={paymentsIds.length}
                itemKey={(index, data) => data[index]}
                itemSize={72}
              >
                {PaymentsListRow}
              </FixedSizeList>
            )}
          </AutoSizer>
        </Table.Tbody>
      </Table>
    </Card>
  )
}

type PaymentsListRowProps = {
  data: Array<Payment["id"]>
  index: number
  style: CSSProperties
}

const PaymentsListRow = memo(({ data, index, style }: PaymentsListRowProps) => {
  const paymentId = data[index]

  return <PaymentListItem key={paymentId} paymentId={paymentId} style={style} />
}, areEqual)

type PaymentsListItemProps = {
  paymentId: Payment["id"]
  style: CSSProperties
}

const PaymentListItem = ({ paymentId, style }: PaymentsListItemProps) => {
  const payment = useAppSelector(selectPaymentById(paymentId))

  const contextMenuRef = usePaymentsContextMenuTrigger({ paymentId })

  const dispatch = useAppDispatch()

  if (!payment) return null

  return (
    <Table.Tr ref={contextMenuRef} style={style}>
      <Table.Td>{payment.client_name || "-"}</Table.Td>

      <Table.Td>{dayjs(payment.created_at).format("DD.MM.YYYY")}</Table.Td>

      <Table.Td className="d-flex">
        {payment.order_uid ? <Clipboard>{payment.order_uid}</Clipboard> : "-"}
        <span className="mx-3">/</span>
        <Clipboard>{payment.transaction_code}</Clipboard>
      </Table.Td>

      <Table.Td>{payment.provider || "-"}</Table.Td>

      <Table.Td>{formatPrice(payment.amount)} zł</Table.Td>

      <Table.Td>
        <Clipboard className="truncate">{payment.user}</Clipboard>
      </Table.Td>

      <Table.Td className="text-center">
        <Tooltip content="Przełącz status płatności">
          <span>
            <Button
              onClick={() => dispatch(togglePaymentStatus(payment.order_id!))}
              color="primary"
              variation="contrast"
              size="small"
              iconProps={{
                name: payment.order_payment_status ? "check" : "close-circle",
                size: "small",
              }}
            />
          </span>
        </Tooltip>
      </Table.Td>

      <Table.Td className="text-right">
        <ContextMenu.Button onClick={openContextMenu} />
      </Table.Td>
    </Table.Tr>
  )
}
